import { useToast } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient, skipToken } from "@tanstack/react-query";
import { Comic } from "../../../types";
import { postToggleFavorite } from "../../../api/comics";
import { useCallback } from "react";
import { useUserData } from "../../../hooks/useUserData";
import { FUNNEL_TYPES } from "../../funnel/utils/contants";
import { setFunnelModalConfig } from "../../funnel/FunnelModal";
import { FunnelContextArea } from "../../funnel/types";
import { t } from "i18next";
import {
  trackComicFavorite,
  trackComicUnfavorite,
} from "../../../analytics/events/engagement";

export function useToggleComicFavorite(
  cacheKey: string,
  modalContext = FunnelContextArea.addToLibrary,
) {
  const toast = useToast();
  const { userData } = useUserData();
  const queryClient = useQueryClient();
  const { data } = useQuery<Comic>({
    queryKey: [cacheKey],
    queryFn: skipToken,
    networkMode: "offlineFirst",
    staleTime: Infinity,
  });

  const { mutate } = useMutation({
    mutationFn: () => {
      return postToggleFavorite(data?.slug ?? "", !data?.is_favorite);
    },
    onMutate: async () => {
      const newFavoriteState = !data?.is_favorite;
      const updatedData = {
        ...data,
        is_favorite: newFavoriteState,
        library_status: newFavoriteState
          ? {
              entity_type: "ComicFavorite",
              comic_id: data?.id ?? 0,
              user_id: userData?.id ?? 0,
              comic_user_reading_status_id: 1,
              disable_notifications: 0,
              disable_emails: 0,
            }
          : null,
      };

      // Update the primary cache key
      await queryClient.setQueryData([cacheKey], updatedData);

      toast({
        status: "success",
        title: `Comic ${newFavoriteState ? "added to" : "removed from"} library`,
      });

      return {
        comicId: data?.id ?? 0,
        newFavoriteState,
      };
    },
    onError: () => {
      // Revert all cache changes on error
      queryClient.invalidateQueries({ queryKey: [cacheKey] });

      toast({
        title: `Unable to ${data?.is_favorite ? "remove from" : "add to"} library`,
        status: "error",
      });
    },
    onSuccess: (_response, _request, context) => {
      void _response;
      void _request;
      if (!context?.comicId) {
        return;
      }

      const source = String(modalContext ?? FunnelContextArea.default);
      if (context.newFavoriteState) {
        trackComicFavorite({
          comicId: context.comicId,
          source,
        });
      } else {
        trackComicUnfavorite({
          comicId: context.comicId,
          source,
        });
      }
    },
  });

  const handleToggleFavorite = useCallback(() => {
    if (userData) {
      mutate();
    } else {
      setFunnelModalConfig({
        type: FUNNEL_TYPES.signup,
        context: {
          modalContext,
        },
        options: {
          onSuccess: () => mutate(),
          welcomeText: t("components.auth.welcomeText.getNotified", {
            artist: data?.name,
          }),
        },
      });
    }
  }, [data, userData, modalContext, mutate]);

  return handleToggleFavorite;
}
